import React, {useEffect, useRef, useState} from "react";
import type {MapData, TripMapHandle} from "./tripMapView";

interface TripMapProps {
    active: boolean;                  // the photos page is showing
    onOpenTrip: (id: string) => void;
}

// "Where I've been" card at the top of the photos page. The map module (and
// the map library with it) is only downloaded once the photos page is open and
// the card is close to being on screen.
export function TripMap({active, onOpenTrip}: TripMapProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<TripMapHandle | null>(null);
    const openRef = useRef(onOpenTrip);
    openRef.current = onOpenTrip;
    const [wanted, setWanted] = useState(false);
    const [view, setView] = useState<"scotland" | "everywhere">("scotland");

    useEffect(() => {
        const card = cardRef.current;
        if (!active || wanted || !card) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setWanted(true);
        }, {root: card.closest(".vignette"), rootMargin: "400px 0px"});
        observer.observe(card);
        return () => observer.disconnect();
    }, [active, wanted]);

    useEffect(() => {
        if (!wanted) return;
        let cancelled = false;
        Promise.all([
            import("./tripMapView"),
            fetch("/map/trips.json").then(r => r.json() as Promise<MapData>),
        ]).then(([view, data]) => {
            if (cancelled || !mapRef.current) return;
            handleRef.current = view.createTripMap(mapRef.current, data, id => openRef.current(id), setView);
        }).catch(() => { /* no map then; the trip list below still works */ });
        return () => {
            cancelled = true;
            handleRef.current?.destroy();
            handleRef.current = null;
        };
    }, [wanted]);

    const show = (v: "scotland" | "everywhere") => {
        setView(v);
        if (v === "scotland") handleRef.current?.showScotland();
        else handleRef.current?.showEverywhere();
    };

    return (
        <div ref={cardRef} className="trip-map-card">
            <div className="trip-map-head">
                <h4>Where I've been</h4>
                <div className="trip-map-toggle">
                    <button type="button" className={view === "scotland" ? "on" : ""} onClick={() => show("scotland")}>Scotland</button>
                    <button type="button" className={view === "everywhere" ? "on" : ""} onClick={() => show("everywhere")}>Everywhere</button>
                </div>
            </div>
            <div ref={mapRef} className="trip-map"/>
            <div className="trip-map-legend">
                <span><i/>cycle route / hiking trail</span>
                <span><i className="ferry"/>ferry</span>
            </div>
        </div>
    );
}
